import { bindable, autoinject, computedFrom } from 'aurelia-framework';
// import * as Plyr from 'plyr';
import * as MimeTypes from 'mime-types';
import { HttpClient } from 'aurelia-http-client';
import { MobileHelper } from "../../../helpers/mobile-helper";
import { Reflection } from '../../../utilities/reflection';
import { RegexHelper } from '../../../helpers/regex-helper';

@autoinject
export class MediaPlayer {

	public containerEl: HTMLDivElement;
	public videoEl: HTMLVideoElement;
	public audioEl: HTMLAudioElement;
	public player: any;
	public current: any;
	public startDownload: boolean = false;
	public isDownloaded: boolean = false;
	private _videoStoped: boolean = true;

	@bindable
	public isVideo: boolean = true;

	@bindable
	public showRestartControl: boolean = false;

	@bindable
	public showSpeedControls: boolean = false;

	@bindable
	public showSettingsControl: boolean = true;

	@bindable
	public showFullTime: boolean = false;

	@bindable
	public link: string;

	@bindable
	public type: string;

	@bindable
	public isDownloadable: boolean = false;

	private get playerElement(): HTMLElement {
		return this.isVideo
			? this.videoEl
			: this.audioEl;
	}

	@computedFrom(Reflection.getPropertyPath<MediaPlayer>(x => x.link))
	public get videoId(): string {
		const youtubeFirstDomain = "www.youtube.com";
		const youtubeSecondDomain = "youtu.be";

		let videoUrl = this.link;
		const domain = this.linkDomain;

		if (domain.indexOf(youtubeFirstDomain) > -1 || domain.indexOf(youtubeSecondDomain) > -1) {
			if (domain.indexOf(youtubeFirstDomain) > -1) {
				videoUrl = this.getParameterFromQueryStringByName("v", this.link);
			}
			if (domain.indexOf(youtubeSecondDomain) > -1) {
				const splited = this.link.split('/');
				videoUrl = splited[splited.length - 1];
			}
		}

		return videoUrl;
	}

	@computedFrom(Reflection.getPropertyPath<MediaPlayer>(x => x.link))
	public get provider(): string {
		const youtubeFirstDomain = "www.youtube.com";
		const youtubeSecondDomain = "youtu.be";
		const vimeoDomain = "vimeo.com"
		
		const url = this.linkDomain;

		if (url.indexOf(youtubeFirstDomain) > -1 || url.indexOf(youtubeSecondDomain) > -1) {
			return "youtube";
		}

		if (url.indexOf(vimeoDomain) > -1) {
			return "vimeo";
		}

		return "";
	}

	@computedFrom(Reflection.getPropertyPath<MediaPlayer>(x => x.link))
	public get linkDomain(): string {
		if(!this.link) return "";
		try
		{
			// this way we can get the domain of the url and check the provider from the domain
			return new URL(this.link).host;
		
		} catch (e) {
			console.error("Error getting domain from link", e);
		}

		return "";
	}

	public constructor(private httpClient: HttpClient) {
		this.httpClient.configure(c => {
			c.withHeader('Access-Control-Allow-Origin', '*');
		})
	}

	private setVideoSource(url: string) {
		const source = {
			src: url
		};
		if (this.provider) {
			source["provider"] = this.provider;
		}
		if(this.type){
			source["type"] = this.type;
		}
		this.player.source = {
			type: 'video',
			sources: [
				source
			],
		};
	}

	private async setUnknownSource(url: string) {

		const sourceInfo = this.sourceInfo(url);
		const sources: any[] = sourceInfo.sources;

		if (sourceInfo && sources.some(s => !!s.type)) {
			this.player.source = sourceInfo;
			return;
		}

		for (let i = 0; i < sources.length; i++) {
			const source:any = sourceInfo.sources[i];
			const response = await this.httpClient.head(source.src);
			source.type = response.headers.get('content-type');

			if (source.type) {
				this.player.source = sourceInfo;
				break;
			}
		}
	}

	private getParameterFromQueryStringByName(name, url): string {
		if (!url) url = window.location.href;
		
		name = RegexHelper.escapeSquareBrackets(name);
		const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
			results = regex.exec(url);
		if (!results) return null;
		if (!results[2]) return '';
		return decodeURIComponent(results[2].replace(/\+/g, ' '));
	}

	public attached() {
		this.setupNewInstance();
	}

	public detached() {
		try {
			if (this.player) {
				this.player.destroy();
			}
		} catch (e) {
			console.error("Error destroying player", e);
		}
	}

	linkChanged() {
		this.setupNewInstance();
	}

	private setupNewInstance() {
		try {
			if (this.player) {
				this.player.destroy();
			}
		}
		catch (e) {
			console.error("Error destroying player", e);
		}

		if (!this.playerElement) {
			return;
		}

		if (this.isVideo) {
			this.playerElement.setAttribute('data-plyr-embed-id', this.videoId);
			this.playerElement.setAttribute('data-plyr-provider', this.provider);
		}

		// this.player = new Plyr(this.playerElement, {
		// 	controls: [
		// 		'play',
		// 		'play-large',
		// 		'progress',
		// 		'mute',
		// 		'volume',
		// 		'fullscreen',
		// 		'current-time',
		// 		this.showRestartControl ? 'restart' : undefined,
		// 		this.showSpeedControls ? 'rewind' : undefined,
		// 		this.showSpeedControls ? 'fast-forward' : undefined,
		// 		this.showFullTime ? 'duration' : undefined,
		// 		this.showSettingsControl ? 'settings' : undefined
		// 	]
		// });

		if (this.isVideo) {
			this.setVideoSource(this.videoId);
		} else {
			this.setUnknownSource(this.link);
		}
	}

	public download() {
		this.player.on("loadeddata", (x) => this.buffered(x));
		this.player.source = this.sourceInfo(this.link);
		this.startDownload = true;
	}

	private buffered(x) {
		if (x.detail.plyr.duration > 0) {
			this.isDownloaded = true;
		}
	}

	preventDefault($event) {
		$event.stopPropagation();

		if (!MobileHelper.isMobileDevice()) return;

		// Make pause start work properly on mobile
		if (!this._videoStoped && this.player.playing) {
			this.player.pause();
			this._videoStoped = true;
		} else {
			this.player.play();
			this._videoStoped = false;
		}
	}

	public sourceInfo(source: string): any {
		const sourceInfo = {
			type: (this.isVideo ? 'video' : 'audio'),
			sources: [{
				src: source,
				type: MimeTypes.lookup(source) as string
			}]
		};

		return sourceInfo;
	}
}
